import type { Solicitacao, Configuracao } from '@/types'

// ── Constantes A4 ────────────────────────────────────────────────────────────
const PAGE_W   = 210
const PAGE_H   = 297
const M        = 18   // margem lateral
const LINE_H   = 5.5  // altura de linha padrão
const CONTENT_Y = 47  // Y inicial após cabeçalho

// ── Paleta ───────────────────────────────────────────────────────────────────
const C_AZUL        = '#0f3b50'
const C_AZUL_CLARO  = '#e8f1f5'
const C_AZUL_BORDA  = '#b8d4e0'
const C_CINZA_900   = '#111827'
const C_CINZA_700   = '#374151'
const C_CINZA_500   = '#6b7280'
const C_CINZA_200   = '#e5e7eb'
const C_CINZA_100   = '#f3f4f6'
const C_BRANCO      = '#ffffff'

// ── URL da logo GEOA ─────────────────────────────────────────────────────────
const GEOA_LOGO_URL = 'https://file.lumi.new/p455908949713027072/SPF2065453480390553600.jpg'

type Doc = {
  setFont: (n: string, s?: string) => void
  setFontSize: (s: number) => void
  setTextColor: (...a: any[]) => void
  setFillColor: (...a: any[]) => void
  setDrawColor: (...a: any[]) => void
  setLineWidth: (w: number) => void
  text: (t: string | string[], x: number, y: number, o?: any) => void
  rect: (x: number, y: number, w: number, h: number, s?: string) => void
  line: (x1: number, y1: number, x2: number, y2: number) => void
  addImage: (d: string, f: string, x: number, y: number, w: number, h: number) => void
  addPage: () => void
  save: (n: string) => void
  getNumberOfPages: () => number
  setPage: (n: number) => void
  splitTextToSize: (t: string, w: number) => string[]
  internal: { pageSize: { width: number; height: number } }
}

function fromHex(h: string): [number, number, number] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
}
const fill  = (d: Doc, c: string) => d.setFillColor(...fromHex(c))
const draw  = (d: Doc, c: string) => d.setDrawColor(...fromHex(c))
const color = (d: Doc, c: string) => d.setTextColor(...fromHex(c))

function loadImageAsDataURL(url: string, bg = '#ffffff'): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width  = img.naturalWidth  || img.width
      canvas.height = img.naturalHeight || img.height
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error(`Falha ao carregar: ${url}`))
    img.src = url
  })
}

function roundedRect(doc: Doc, x: number, y: number, w: number, h: number, r: number, style: string) {
  const d = doc as any
  if (typeof d.roundedRect === 'function') {
    d.roundedRect(x, y, w, h, r, r, style)
  } else {
    doc.rect(x, y, w, h, style)
  }
}

// ── Cabeçalho institucional ──────────────────────────────────────────────────
function drawHeader(doc: Doc, cfg: Configuracao | null, geoaDataURL?: string) {
  const HDR_H = 33
  const SUB_H = 7.5

  fill(doc, C_BRANCO)
  doc.rect(0, 0, PAGE_W, HDR_H + SUB_H + 1.2, 'F')

  // Logo Prefeitura
  const logoSize = 22
  const logoX    = M
  const logoY    = (HDR_H - logoSize) / 2

  if (cfg?.logoUrl) {
    try { doc.addImage(cfg.logoUrl, 'PNG', logoX, logoY, logoSize, logoSize) } catch {
      try { doc.addImage(cfg.logoUrl, 'JPEG', logoX, logoY, logoSize, logoSize) } catch { /**/ }
    }
  } else {
    fill(doc, C_AZUL_CLARO)
    draw(doc, C_AZUL_BORDA)
    doc.setLineWidth(0.1)
    doc.rect(logoX, logoY, logoSize, logoSize, 'FD')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    color(doc, C_AZUL)
    doc.text('PM', logoX + logoSize / 2, logoY + logoSize / 2 + 1.5, { align: 'center' })
  }

  // Logo GEOA
  const geoa_W = 37, geoa_H = 15
  const geoa_X = PAGE_W - M - geoa_W
  const geoa_Y = (HDR_H - geoa_H) / 2

  fill(doc, C_BRANCO)
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.4)
  doc.rect(geoa_X - 2, geoa_Y - 2, geoa_W + 4, geoa_H + 4, 'FD')
  if (geoaDataURL) {
    try { doc.addImage(geoaDataURL, 'PNG', geoa_X, geoa_Y, geoa_W, geoa_H) } catch { /**/ }
  } else {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    color(doc, C_AZUL)
    doc.text('GEOA', geoa_X + geoa_W / 2, geoa_Y + geoa_H / 2 + 1, { align: 'center' })
  }

  // Textos institucionais
  const textX    = logoX + logoSize + 5
  const textMaxW = geoa_X - 4 - textX

  const nomePref = (cfg?.nomePrefeitura || 'PREFEITURA MUNICIPAL DE SÃO JOSÉ DO SERIDÓ').toUpperCase()
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  color(doc, C_AZUL)
  doc.text(nomePref, textX, 9, { maxWidth: textMaxW })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  color(doc, C_CINZA_700)
  doc.text('Secretaria Municipal de Meio Ambiente', textX, 15)

  const endParts: string[] = []
  if (cfg?.endereco) endParts.push(cfg.endereco)
  if (cfg?.cep)      endParts.push(`CEP: ${cfg.cep}`)
  if (endParts.length) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    color(doc, C_CINZA_500)
    doc.text(endParts.join('   |   '), textX, 20.5, { maxWidth: textMaxW })
  }

  const contatos: string[] = []
  if (cfg?.cnpj)     contatos.push(`CNPJ: ${cfg.cnpj}`)
  if (cfg?.telefone) contatos.push(`Tel: ${cfg.telefone}`)
  if (cfg?.email)    contatos.push(cfg.email)
  if (contatos.length) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    color(doc, C_CINZA_500)
    doc.text(contatos.join('   |   '), textX, 26, { maxWidth: textMaxW })
  }

  // Linha + faixa de título
  fill(doc, C_AZUL)
  doc.rect(0, HDR_H, PAGE_W, 1.2, 'F')

  fill(doc, C_AZUL_CLARO)
  doc.rect(0, HDR_H + 1.2, PAGE_W, SUB_H, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6.8)
  color(doc, C_AZUL)
  doc.text(
    'GEOA – Gestão Online Ambiental  |  Sistema de Remoção de Árvores  –  São José do Seridó/RN',
    PAGE_W / 2, HDR_H + 1.2 + SUB_H - 2, { align: 'center' }
  )
}

// ── Limite inferior de conteúdo (antes do rodapé) ───────────────────────────
const CONTENT_BOTTOM = PAGE_H - 14   // espaço reservado para o rodapé

// ── Quebra de página automática ──────────────────────────────────────────────
function checkPageBreak(
  doc: Doc,
  y: number,
  needed: number,
  cfg: Configuracao | null,
  geoaDataURL?: string,
  pageTracker?: { current: number }
): number {
  if (y + needed > CONTENT_BOTTOM) {
    doc.addPage()
    if (pageTracker) pageTracker.current++
    drawHeader(doc, cfg, geoaDataURL)
    return CONTENT_Y
  }
  return y
}

// ── Rodapé ───────────────────────────────────────────────────────────────────
function drawFooter(doc: Doc, protocolo: string, pg: number, total: number) {
  fill(doc, C_AZUL)
  doc.rect(0, PAGE_H - 11, PAGE_W, 0.8, 'F')
  fill(doc, C_AZUL_CLARO)
  doc.rect(0, PAGE_H - 10.2, PAGE_W, 10.2, 'F')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  color(doc, C_CINZA_500)
  doc.text(`Protocolo: ${protocolo}`, M + 2, PAGE_H - 4)
  doc.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, PAGE_W / 2, PAGE_H - 4, { align: 'center' })
  doc.text(`Página ${pg} de ${total}`, PAGE_W - M, PAGE_H - 4, { align: 'right' })
}

// ── Título de secção ──────────────────────────────────────────────────────────
function secTitle(doc: Doc, num: string, title: string, y: number): number {
  const bW = PAGE_W - M * 2
  const bH = 8

  fill(doc, C_AZUL)
  roundedRect(doc, M, y, bW, bH, 2, 'F')

  // Número da secção em caixa branca à esquerda
  fill(doc, C_BRANCO)
  roundedRect(doc, M + 3, y + 1.5, 10, 5, 1, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  color(doc, C_AZUL)
  doc.text(num, M + 8, y + 5.2, { align: 'center' })

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.2)
  doc.setTextColor(255, 255, 255)
  doc.text(title, M + 17, y + 5.4)

  return y + bH + 3
}

// ── Linha de campo com linha de preenchimento ─────────────────────────────────
function fieldLine(doc: Doc, label: string, value: string, x: number, y: number, w: number): number {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  color(doc, C_AZUL)
  doc.text(label + ':', x, y)

  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.2)
  doc.line(x, y + 1, x + w, y + 1)

  const lines = doc.splitTextToSize(value || '', w)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  color(doc, C_CINZA_900)

  if (lines.length === 1) {
    doc.text(lines[0], x, y - 0.5)
  } else {
    lines.forEach((l: string, i: number) => {
      doc.text(l, x, y - 0.5 + i * LINE_H)
    })
  }

  return y + lines.length * LINE_H + 3
}

// ── Caixa de campo moderno — com suporte a quebra de página ──────────────────
function fieldBox(
  doc: Doc,
  label: string,
  value: string,
  x: number,
  y: number,
  w: number,
  cfg?: Configuracao | null,
  geoaDataURL?: string,
  pages?: { current: number }
): number {
  const LINE_H_BOX = 4.2
  const HEADER_H   = 9    // label + separador
  const PAD_V      = 3    // padding inferior
  const MAX_BOX_H  = CONTENT_BOTTOM - y - 4  // máximo até o rodapé

  const allLines = doc.splitTextToSize(value || '—', w - 8)

  // Calcula quantas linhas cabem na altura disponível
  const maxLinesPerBlock = Math.max(1, Math.floor((MAX_BOX_H - HEADER_H - PAD_V) / LINE_H_BOX))

  // Divide em blocos se necessário
  const blocks: string[][] = []
  for (let i = 0; i < allLines.length; i += maxLinesPerBlock) {
    blocks.push(allLines.slice(i, i + maxLinesPerBlock))
  }
  if (blocks.length === 0) blocks.push(['—'])

  let curY = y
  for (let bi = 0; bi < blocks.length; bi++) {
    const chunk  = blocks[bi]
    const boxH   = Math.max(chunk.length * LINE_H_BOX + HEADER_H + PAD_V, 14)

    // Quebra de página se não couber
    if (cfg !== undefined && curY + boxH > CONTENT_BOTTOM) {
      doc.addPage()
      if (pages) pages.current++
      drawHeader(doc, cfg ?? null, geoaDataURL)
      curY = CONTENT_Y
    }

    fill(doc, C_CINZA_100)
    draw(doc, C_AZUL_BORDA)
    doc.setLineWidth(0.18)
    roundedRect(doc, x, curY, w, boxH, 2, 'FD')

    fill(doc, C_AZUL)
    roundedRect(doc, x, curY, 2.5, boxH, 1, 'F')
    fill(doc, C_CINZA_100)
    doc.rect(x + 1.5, curY + 0.5, 1.5, boxH - 1, 'F')

    // Label apenas no primeiro bloco
    if (bi === 0) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(5.5)
      color(doc, '#1a5470')
      doc.text(label.toUpperCase(), x + 5.5, curY + 4)
      draw(doc, C_CINZA_200)
      doc.setLineWidth(0.08)
      doc.line(x + 5.5, curY + 5.5, x + w - 3, curY + 5.5)
    } else {
      // Continuação — sem label, começa texto mais alto
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(5)
      color(doc, C_CINZA_500)
      doc.text('(continuação)', x + 5.5, curY + 4)
      draw(doc, C_CINZA_200)
      doc.setLineWidth(0.08)
      doc.line(x + 5.5, curY + 5.5, x + w - 3, curY + 5.5)
    }

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    color(doc, C_CINZA_900)
    doc.text(chunk, x + 5.5, curY + 9)

    curY += boxH + 2.5
  }

  return curY
}

// ── Checkbox visual ───────────────────────────────────────────────────────────
function checkbox(doc: Doc, label: string, checked: boolean, x: number, y: number): number {
  const boxSize = 4
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.3)
  roundedRect(doc, x, y - boxSize + 0.5, boxSize, boxSize, 0.8, 'D')

  if (checked) {
    fill(doc, C_AZUL)
    roundedRect(doc, x + 0.8, y - boxSize + 1.3, boxSize - 1.6, boxSize - 1.6, 0.5, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(255, 255, 255)
    doc.text('✓', x + 0.9, y - 0.2)
  }

  doc.setFont('helvetica', checked ? 'bold' : 'normal')
  doc.setFontSize(8.5)
  color(doc, checked ? C_CINZA_900 : C_CINZA_500)
  doc.text(label, x + boxSize + 2.5, y)

  return y + 6
}

// ── Linha de assinatura ───────────────────────────────────────────────────────
function assinatura(doc: Doc, nome: string, cargo: string, registro: string, x: number, y: number, w: number): void {
  // Espaço para assinatura
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.15)
  for (let px = x; px < x + w; px += 3) {
    doc.line(px, y, Math.min(px + 1.5, x + w), y)
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  color(doc, C_CINZA_900)
  doc.text(nome, x + w / 2, y + 5, { align: 'center' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  color(doc, C_CINZA_500)
  if (cargo) doc.text(cargo, x + w / 2, y + 9.5, { align: 'center' })
  if (registro) doc.text(registro, x + w / 2, y + 13.5, { align: 'center' })
}

// ── Utilitário de data por extenso ───────────────────────────────────────────
function dataExtenso(): string {
  const d = new Date()
  const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro']
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`
}

function anoAtual(): string {
  return String(new Date().getFullYear())
}

// ── Número de parecer (AAAA/sequencial baseado no protocolo) ─────────────────
function numeroParecer(protocolo: string): string {
  // Extrai a parte numérica do sufixo e converte para número
  // Ex: '20260609-A1B2C3' → usa os 6 últimos chars numéricos formatados
  const suffix = protocolo?.split('-')?.[1] || '001'
  const numericOnly = suffix.replace(/\D/g, '').slice(-4).replace(/^0+/, '') || '1'
  return `${numericOnly.padStart(3, '0')}/${anoAtual()}`
}

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÃO PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export interface DadosParecer {
  dataVistoria: string
  responsavelTecnico: string
  cargoResponsavel: string
  registroProfissional: string
  condicoes: {
    inclinacao: boolean
    danoRaizes: boolean
    interferencia: boolean
    cavidades: boolean
    riscoQueda: boolean
    outrosFatores: string
  }
  favoravel: boolean | null
  justificativa: string
  parecerFinal: string
}

export async function gerarParecerTecnico(sol: Solicitacao, cfg: Configuracao | null, dados?: DadosParecer) {
  const { default: JsPDF } = await import('jspdf')
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as unknown as Doc

  const W_INNER   = PAGE_W - M * 2
  const COL_W     = (W_INNER - 6) / 2

  // Rastreador de páginas para rodapé dinâmico
  const pages = { current: 1 }

  // Helper local com contexto fechado
  const pgBreak = (y: number, needed: number): number =>
    checkPageBreak(doc, y, needed, cfg, geoaDataURL, pages)

  let geoaDataURL: string | undefined
  try { geoaDataURL = await loadImageAsDataURL(GEOA_LOGO_URL, '#ffffff') } catch { /**/ }

  // ─────────────────────────────────────────────────────────────────────────
  // PÁGINA 1
  // ─────────────────────────────────────────────────────────────────────────
  drawHeader(doc, cfg, geoaDataURL)
  let y = CONTENT_Y

  // ── Título principal do documento ──────────────────────────────────────
  fill(doc, C_AZUL)
  roundedRect(doc, M, y, W_INNER, 12, 2, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(255, 255, 255)
  doc.text(`PARECER TÉCNICO Nº ${numeroParecer(sol.protocolo)}`, PAGE_W / 2, y + 5, { align: 'center' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  doc.setTextColor(180, 210, 225)
  doc.text('SECRETARIA MUNICIPAL DE MEIO AMBIENTE  –  MUNICÍPIO DE SÃO JOSÉ DO SERIDÓ/RN', PAGE_W / 2, y + 9.5, { align: 'center' })
  y += 17

  // ── 1. IDENTIFICAÇÃO ───────────────────────────────────────────────────
  y = secTitle(doc, '1', 'IDENTIFICAÇÃO', y)

  let y1 = y, y2 = y
  y1 = fieldBox(doc, 'Requerente', sol.nomeCompleto, M, y1, COL_W)
  y2 = fieldBox(doc, 'CPF', sol.cpf, M + COL_W + 6, y2, COL_W)
  y = Math.max(y1, y2) + 1

  y1 = y; y2 = y
  const enderecoCompleto = [
    sol.ruaNome, sol.numero ? `nº ${sol.numero}` : '',
    sol.bairroNome,
    (sol as any).cidadeSolicitante || 'São José do Seridó/RN',
  ].filter(Boolean).join(', ')
  y1 = fieldBox(doc, 'Endereço', enderecoCompleto, M, y1, COL_W)
  y2 = fieldBox(doc, 'Protocolo', sol.protocolo, M + COL_W + 6, y2, COL_W)
  y = Math.max(y1, y2) + 1

  y1 = y; y2 = y
  y1 = fieldBox(doc, 'Data da Vistoria', dados?.dataVistoria || '_____ / _____ / ___________', M, y1, COL_W)
  y2 = fieldBox(doc, 'Responsável Técnico', dados?.responsavelTecnico || '________________________________', M + COL_W + 6, y2, COL_W)
  y = Math.max(y1, y2) + 1

  y1 = y; y2 = y
  y1 = fieldBox(doc, 'Cargo', dados?.cargoResponsavel || '________________________________', M, y1, COL_W)
  y2 = fieldBox(doc, 'Registro Profissional', dados?.registroProfissional || '________________________________', M + COL_W + 6, y2, COL_W)
  y = Math.max(y1, y2) + 3

  // ── 2. OBJETO ──────────────────────────────────────────────────────────
  y = secTitle(doc, '2', 'OBJETO', y)

  const objetoText = `Análise técnica referente à solicitação de remoção de indivíduo arbóreo localizado no imóvel acima identificado, conforme requerimento protocolado sob nº ${sol.protocolo}.`
  fill(doc, C_CINZA_100)
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.1)
  const objetoLines = doc.splitTextToSize(objetoText, W_INNER - 10)
  const objetoH = objetoLines.length * 4 + 8
  roundedRect(doc, M, y, W_INNER, objetoH, 2, 'FD')
  fill(doc, C_AZUL)
  doc.rect(M, y, 2.5, objetoH, 'F')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  color(doc, C_CINZA_700)
  doc.text(objetoLines, M + 6, y + 5)
  y += objetoH + 4

  // ── 3. CARACTERIZAÇÃO DA ÁRVORE ────────────────────────────────────────
  y = secTitle(doc, '3', 'CARACTERIZAÇÃO DA ÁRVORE', y)

  y1 = y; y2 = y
  y1 = fieldBox(doc, 'Espécie / Tipo', sol.tipoArvore, M, y1, COL_W)
  y2 = fieldBox(doc, 'Quantidade', String(sol.quantidadeArvores), M + COL_W + 6, y2, COL_W)
  y = Math.max(y1, y2) + 1

  // ── Porte com checkboxes (agora campo direto da solicitação) ───────────
  const porteOpts: ('Pequeno' | 'Médio' | 'Grande')[] = ['Pequeno', 'Médio', 'Grande']
  const porteSolicitacao = (sol as any).porte as string | undefined

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6.5)
  color(doc, '#1a5470')
  doc.text('PORTE:', M, y + 4)

  let px = M + 14
  for (const opt of porteOpts) {
    const isChecked = opt === porteSolicitacao
    draw(doc, C_AZUL_BORDA)
    doc.setLineWidth(0.3)
    roundedRect(doc, px, y + 0.5, 4, 4, 0.8, 'D')
    if (isChecked) {
      fill(doc, C_AZUL)
      roundedRect(doc, px + 0.8, y + 1.3, 2.4, 2.4, 0.5, 'F')
    }
    doc.setFont('helvetica', isChecked ? 'bold' : 'normal')
    doc.setFontSize(8)
    color(doc, isChecked ? C_CINZA_900 : C_CINZA_500)
    doc.text(opt, px + 5.5, y + 4)
    px += 26
  }
  y += 9

  // Estado fitossanitário
  y1 = y; y2 = y
  y1 = fieldBox(doc, 'Estado Fitossanitário', sol.estadoArvore || '—', M, y1, COL_W)

  // Coordenadas — só aparece se preenchida
  const coordenadas = (sol as any).coordenadas as string | undefined
  if (coordenadas && coordenadas.trim()) {
    y2 = fieldBox(doc, 'Coordenadas GPS', coordenadas, M + COL_W + 6, y2, COL_W)
  }
  y = Math.max(y1, y2) + 1

  // ─────────────────────────────────────────────────────────────────────────
  // PÁGINA 2 — a partir daqui tudo usa pgBreak antes de cada bloco
  // ─────────────────────────────────────────────────────────────────────────
  doc.addPage()
  pages.current++
  drawHeader(doc, cfg, geoaDataURL)
  y = CONTENT_Y

  // ── 4. VISTORIA TÉCNICA ────────────────────────────────────────────────
  y = pgBreak(y, 14)
  y = secTitle(doc, '4', 'VISTORIA TÉCNICA', y)

  const introText = 'Durante a inspeção realizada in loco, foram observadas as seguintes condições:'
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  color(doc, C_CINZA_700)
  doc.text(introText, M, y)
  y += 6

  // Checkboxes de condições observadas
  const condicoes = [
    { label: 'Presença de inclinação acentuada do tronco', check: dados ? dados.condicoes.inclinacao : sol.estadoArvore === 'Risco de Queda' },
    { label: 'Danos estruturais em raízes superficiais', check: dados ? dados.condicoes.danoRaizes : sol.estadoArvore === 'Danificando Estrutura' },
    { label: 'Interferência com edificações, muros ou redes de infraestrutura urbana', check: dados ? dados.condicoes.interferencia : sol.estadoArvore === 'Danificando Estrutura' },
    { label: 'Presença de cavidades, cupins, fungos ou apodrecimento interno', check: dados ? dados.condicoes.cavidades : sol.estadoArvore === 'Doente' || sol.estadoArvore === 'Morta' },
    { label: 'Risco potencial de queda de galhos ou do indivíduo arbóreo', check: dados ? dados.condicoes.riscoQueda : sol.estadoArvore === 'Risco de Queda' || sol.estadoArvore === 'Morta' },
  ]

  fill(doc, C_CINZA_100)
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.1)
  const condicoesH = condicoes.length * 6 + 14
  y = pgBreak(y, condicoesH + 20)
  roundedRect(doc, M, y, W_INNER, condicoesH, 2, 'FD')
  fill(doc, C_AZUL)
  doc.rect(M, y, 2.5, condicoesH, 'F')

  let cy = y + 6
  for (const c of condicoes) {
    cy = checkbox(doc, c.label, c.check, M + 6, cy)
  }

  // Campo "Outros fatores"
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  color(doc, C_CINZA_700)
  const outrosText = dados?.condicoes.outrosFatores ? `Outros fatores observados: ${dados.condicoes.outrosFatores}` : 'Outros fatores observados: _______________________________________________'
  doc.text(outrosText, M + 6, cy)
  y += condicoesH + 3

  // Conclusão da vistoria — só aparece se houver relato
  const relato = sol.relatoProblema?.trim()
  if (relato) {
    const conclusaoVistoria = `Foi constatado que o indivíduo apresenta condições que comprometem sua estabilidade e segurança, representando risco ao patrimônio público e/ou privado e à integridade física da população.\n\nRelato do solicitante: ${relato}`
    const conclusaoLines = doc.splitTextToSize(conclusaoVistoria, W_INNER - 10)
    const conclusaoH = conclusaoLines.length * 3.8 + 8
    y = pgBreak(y, conclusaoH + 15)
    fill(doc, '#eff6ff')
    draw(doc, '#bfdbfe')
    doc.setLineWidth(0.15)
    roundedRect(doc, M, y, W_INNER, conclusaoH, 2, 'FD')
    fill(doc, '#1d4ed8')
    doc.rect(M, y, 2.5, conclusaoH, 'F')
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    color(doc, '#1e3a5f')
    doc.text(conclusaoLines, M + 6, y + 5)
    y += conclusaoH + 4
  }

  // ── 5. FUNDAMENTAÇÃO LEGAL ─────────────────────────────────────────────
  y = pgBreak(y, 50)
  y = secTitle(doc, '5', 'FUNDAMENTAÇÃO LEGAL', y)

  const legalText = 'Considerando as disposições da Lei Municipal nº 426/2018 – Código de Arborização Municipal de São José do Seridó/RN, que disciplina os atos administrativos e técnicos relacionados à arborização urbana, bem como os procedimentos de vistoria, fiscalização e autorização para manejo arbóreo, conclui-se que a supressão poderá ser autorizada mediante parecer técnico favorável e compensação ambiental correspondente.'
  const legalLines = doc.splitTextToSize(legalText, W_INNER - 10)
  const legalH = legalLines.length * 3.8 + 8
  y = pgBreak(y, legalH + 15)
  fill(doc, '#f0fdf4')
  draw(doc, '#86efac')
  doc.setLineWidth(0.15)
  roundedRect(doc, M, y, W_INNER, legalH, 2, 'FD')
  fill(doc, '#16a34a')
  doc.rect(M, y, 2.5, legalH, 'F')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  color(doc, '#14532d')
  doc.text(legalLines, M + 6, y + 5)
  y += legalH + 4

  // ── 6. CONCLUSÃO ───────────────────────────────────────────────────────
  y = pgBreak(y, 65)
  y = secTitle(doc, '6', 'CONCLUSÃO', y)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  color(doc, C_CINZA_700)
  doc.text('Após análise técnica realizada no local, este parecer é:', M, y)
  y += 6

  // Checkboxes Favorável / Desfavorável
  const isFavoravel = dados?.favoravel === true
  const isDesfavoravel = dados?.favoravel === false
  const favH = 22
  fill(doc, C_CINZA_100)
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.12)
  roundedRect(doc, M, y, W_INNER, favH, 2, 'FD')

  // Favorável
  if (isFavoravel) {
    fill(doc, '#dcfce7')
    roundedRect(doc, M + 4, y + 3, W_INNER / 2 - 8, 7, 1.5, 'F')
  }
  draw(doc, isFavoravel ? '#16a34a' : '#86efac')
  doc.setLineWidth(0.4)
  roundedRect(doc, M + 6, y + 4.5, 5, 5, 1, 'D')
  if (isFavoravel) {
    fill(doc, '#16a34a')
    roundedRect(doc, M + 6.8, y + 5.3, 3.4, 3.4, 0.6, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(255,255,255)
    doc.text('✓', M + 7, y + 8)
  }
  doc.setFont('helvetica', isFavoravel ? 'bold' : 'normal')
  doc.setFontSize(9)
  color(doc, isFavoravel ? '#15803d' : '#6b7280')
  doc.text('FAVORÁVEL À REMOÇÃO', M + 14, y + 8.5)

  // Desfavorável
  if (isDesfavoravel) {
    fill(doc, '#fee2e2')
    roundedRect(doc, M + 4, y + 13, W_INNER / 2 - 8, 7, 1.5, 'F')
  }
  draw(doc, isDesfavoravel ? '#b91c1c' : '#fca5a5')
  doc.setLineWidth(0.4)
  roundedRect(doc, M + 6, y + 14, 5, 5, 1, 'D')
  if (isDesfavoravel) {
    fill(doc, '#b91c1c')
    roundedRect(doc, M + 6.8, y + 14.8, 3.4, 3.4, 0.6, 'F')
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(255,255,255)
    doc.text('✓', M + 7, y + 17.5)
  }
  doc.setFont('helvetica', isDesfavoravel ? 'bold' : 'normal')
  doc.setFontSize(9)
  color(doc, isDesfavoravel ? '#b91c1c' : '#6b7280')
  doc.text('DESFAVORÁVEL À REMOÇÃO', M + 14, y + 17.5)
  y += favH + 3

  // Justificativa — usa apenas o que foi preenchido no modal (com suporte a quebra de página)
  const justText = dados?.justificativa?.trim() || ''
  if (justText) {
    y = pgBreak(y, 20)
    y = fieldBox(doc, 'Justificativa Técnica', justText, M, y, W_INNER, cfg, geoaDataURL, pages)
    y += 3
  }

  // ── 7. MEDIDA COMPENSATÓRIA — só aparece quando favorável ──────────────
  let secFinal = '7'
  if (dados?.favoravel === true) {
    secFinal = '8'
    y = pgBreak(y, 45)
    y = secTitle(doc, '7', 'MEDIDA COMPENSATÓRIA', y)

    const qtd = sol.quantidadeArvores || 1
    const compText = `Como compensação ambiental pela remoção autorizada, recomenda-se o plantio de ${qtd} (${qtd === 1 ? 'uma' : qtd + ' mudas'}) muda${qtd > 1 ? 's' : ''} de espécies nativas adequadas à arborização urbana, em local definido pela Secretaria Municipal de Meio Ambiente, conforme determina o Código de Arborização Municipal.`
    const compLines = doc.splitTextToSize(compText, W_INNER - 10)
    const compH = compLines.length * 3.8 + 8
    y = pgBreak(y, compH + 15)
    fill(doc, '#fff7ed')
    draw(doc, '#fed7aa')
    doc.setLineWidth(0.15)
    roundedRect(doc, M, y, W_INNER, compH, 2, 'FD')
    fill(doc, '#ea580c')
    doc.rect(M, y, 2.5, compH, 'F')
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    color(doc, '#7c2d12')
    doc.text(compLines, M + 6, y + 5)
    y += compH + 4
  }

  // ── PARECER FINAL (numeração dinâmica) ─────────────────────────────────
  y = pgBreak(y, 55)
  y = secTitle(doc, secFinal, 'PARECER FINAL', y)

  const finalText = dados?.parecerFinal ||
    (dados?.favoravel === false
      ? 'Diante das condições verificadas e da fundamentação legal aplicável, este parecer conclui pelo INDEFERIMENTO do pedido de remoção do indivíduo arbóreo descrito neste documento, por não apresentar condições que justifiquem a supressão.'
      : 'Diante das condições verificadas e da fundamentação legal aplicável, este parecer conclui pela AUTORIZAÇÃO DA REMOÇÃO do indivíduo arbóreo descrito neste documento, condicionada ao cumprimento das medidas compensatórias estabelecidas.')

  // Parecer final com suporte a quebra de página para textos longos
  {
    const allFinalLines = doc.splitTextToSize(finalText, W_INNER - 10)
    const LINE_H_F = 3.8
    const HDR_F    = 8
    const maxPerPage = Math.max(1, Math.floor((CONTENT_BOTTOM - y - HDR_F - 15) / LINE_H_F))
    const finalBlocks: string[][] = []
    for (let i = 0; i < allFinalLines.length; i += maxPerPage) {
      finalBlocks.push(allFinalLines.slice(i, i + maxPerPage))
    }
    if (finalBlocks.length === 0) finalBlocks.push([finalText])

    for (let bi = 0; bi < finalBlocks.length; bi++) {
      const chunk  = finalBlocks[bi]
      const finalH = chunk.length * LINE_H_F + HDR_F
      y = pgBreak(y, finalH + 15)
      fill(doc, C_AZUL_CLARO)
      draw(doc, C_AZUL_BORDA)
      doc.setLineWidth(0.15)
      roundedRect(doc, M, y, W_INNER, finalH, 2, 'FD')
      fill(doc, C_AZUL)
      doc.rect(M, y, 2.5, finalH, 'F')
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7.5)
      color(doc, C_AZUL)
      doc.text(chunk, M + 6, y + 5)
      y += finalH + 5
    }
  }

  // ── Data e local + Assinaturas ─────────────────────────────────────────
  // Garante que data + 2 blocos de assinatura (cada ~35mm) cabem na página
  y = pgBreak(y, 80)

  // Separador antes da data
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.1)
  doc.line(M, y, PAGE_W - M, y)
  y += 6

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  color(doc, C_CINZA_700)
  doc.text(`São José do Seridó/RN, ${dataExtenso()}.`, PAGE_W / 2, y, { align: 'center' })
  y += 14

  // Caixas de assinatura com fundo visual
  const sigW   = (W_INNER - 10) / 2
  const sig1_X = M
  const sig2_X = M + sigW + 10
  const sigBoxH = 32

  // Fundo leve para cada bloco de assinatura
  fill(doc, C_CINZA_100)
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.12)
  roundedRect(doc, sig1_X, y, sigW, sigBoxH, 2, 'FD')
  roundedRect(doc, sig2_X, y, sigW, sigBoxH, 2, 'FD')

  // Linha de assinatura dentro de cada caixa
  const nomeResp = dados?.responsavelTecnico || '________________________________'
  const cargoResp = dados?.cargoResponsavel
    ? `${dados.cargoResponsavel}${dados.registroProfissional ? '  –  ' + dados.registroProfissional : ''}`
    : 'Cargo / Registro Profissional'
  assinatura(doc, nomeResp, 'Responsável Técnico', cargoResp, sig1_X, y + 18, sigW)

  // Segunda assinatura: Prefeito Municipal (exclusiva do Parecer Técnico)
  assinatura(doc, 'Jackson Dantas', 'Prefeito Municipal', 'Município de São José do Seridó/RN', sig2_X, y + 18, sigW)

  // ── Rodapés em todas as páginas ──────────────────────────────────────────
  const totalPages = doc.getNumberOfPages()
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p)
    drawFooter(doc, sol.protocolo, p, totalPages)
  }

  doc.save(`ParecerTecnico_${sol.protocolo}.pdf`)
}
